'use client'

import { useScreenMatch } from 'lib/utilities/screens'
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { BookingWidgetProps } from '../BookingWidget/BookingWidget'
import HomepageHeroDesktop from './HomepageHeroDesktop'
import HomepageHeroMobile from './HomepageHeroMobile'

export interface Steps {
  header: string
  subheader: string
}

interface HomepageHeroButton {
  text: string
  onClick: () => void
}

export interface HomepageHeroProps {
  /**
   * Header of the component
   */
  header: string | ReactNode
  /**
   * Sub Header of the component
   */
  subheader: string
  /**
   * Background image of the component
   */
  image: string
  /**
   * Accessible description for the background image
   */
  imageAlt?: string
  /**
   * Optional CTA of the component
   */
  button?: HomepageHeroButton
  /**
   * Steps Header of the component
   */
  stepsHeader: string | ReactNode
  /**
   * Steps of of the component
   * Accepts array of items
   * eg: [{ header: 'Lorem Ipsum' subheader: 'Lorem Ipsum' }]
   */
  steps: Steps[]
  /**
   * Booking widget data props
   */
  bookingWidget?: BookingWidgetProps
}

export default function HomepageHero({
  header,
  subheader,
  image,
  imageAlt,
  button,
  stepsHeader,
  steps,
  bookingWidget,
}: HomepageHeroProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const getHeaderElement = () =>
    typeof window === 'undefined'
      ? null
      : (document.querySelector('header') as HTMLElement | null)

  const [clientWidth, setClientWidth] = useState(0)
  const [clientHeight, setClientHeight] = useState(0)
  const [siteHeaderHeight, setSiteHeaderHeight] = useState(0)
  const [brazeBannerHeight, setBrazeBannerHeight] = useState(0)
  const isDesktop = useScreenMatch('lg')

  useLayoutEffect(() => {
    const header = getHeaderElement()
    if (!header) return
    setSiteHeaderHeight(header.offsetHeight ?? 0)
    const observer = new ResizeObserver(() => {
      setSiteHeaderHeight(header.offsetHeight ?? 0)
    })
    observer.observe(header)
    return () => observer.disconnect()
  }, [])

  // Listen for Braze banner height changes so we can offset content correctly post-render
  useEffect(() => {
    const onBannerHeight = (e: Event): void => {
      const height = (e as CustomEvent<{ height: number }>).detail?.height ?? 0
      setBrazeBannerHeight(height)
    }
    window.addEventListener(
      'brazeBannerHeightChange',
      onBannerHeight as EventListener,
    )
    // Also read any existing CSS var on mount (in case banner already measured)
    const cssVar = getComputedStyle(document.documentElement).getPropertyValue(
      '--braze-banner-height',
    )
    if (cssVar) {
      const parsed = parseFloat(cssVar)
      if (!Number.isNaN(parsed)) setBrazeBannerHeight(parsed)
    }
    return () => {
      window.removeEventListener(
        'brazeBannerHeightChange',
        onBannerHeight as EventListener,
      )
    }
  }, [])

  useLayoutEffect(() => {
    const initSetValueFn = () => {
      setClientWidth(document.documentElement.clientWidth)
      setClientHeight(document.documentElement.clientHeight)
    }
    initSetValueFn()
    window.addEventListener('resize', initSetValueFn)
    return () => window.removeEventListener('resize', initSetValueFn)
  }, [])

  return (
    <section className="relative bg-white contain-paint" ref={sectionRef}>
      {isDesktop ? (
        <HomepageHeroDesktop
          header={header}
          subheader={subheader}
          image={image}
          imageAlt={imageAlt}
          stepsHeader={stepsHeader}
          steps={steps}
          bookingWidget={bookingWidget}
          clientWidth={clientWidth}
          clientHeight={clientHeight}
          siteHeaderHeight={siteHeaderHeight}
          bannerHeight={brazeBannerHeight}
          sectionRef={sectionRef}
        />
      ) : (
        <HomepageHeroMobile
          header={header}
          subheader={subheader}
          image={image}
          imageAlt={imageAlt}
          button={button}
          stepsHeader={stepsHeader}
          steps={steps}
          bookingWidget={bookingWidget}
          clientHeight={clientHeight}
          siteHeaderHeight={siteHeaderHeight}
          sectionRef={sectionRef}
        />
      )}
    </section>
  )
}
